import axios from 'axios';
import { Cookies } from 'react-cookie';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const api = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor - add auth token
api.interceptors.request.use(
    (config) => {
        const cookies = new Cookies();
        const user = cookies.get('user');
        if (user && user.token) {
            config.headers.Authorization = `PostmanFraker ${user.token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor - handle 401
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            const cookies = new Cookies();
            cookies.remove('user', { path: '/' });
            if (window.location.pathname !== '/account/login') {
                window.location.href = '/account/login';
            }
        }
        return Promise.reject(error);
    }
);

// ---- Auth ----
export const authAPI = {
    login: (username, password) => api.post('/login', { username, password }),
    register: (fullname, email, password) => api.post('/register', { fullname, email, password }),
    verifyEmail: (email, code) => api.post('/verify-email', { email, code }),
    forgotPassword: (email) => api.post('/forgot-password', { email }),
    resetPassword: (email, code, newPassword) => api.post('/reset-password', { email, code, newPassword }),
    getProfile: () => api.get('/me'),
    logout: () => api.get('/logout'),
};

// ---- Devices ----
export const devicesAPI = {
    getAll: () => api.get('/get-devices'),
    connect: (id) => api.get(`/connect-device/${id}`),
    disconnect: (id) => api.get(`/disconnect-device/${id}`),
    delete: (id) => api.delete(`/api/devices/${id}`),
    getStats: (id) => api.get(`/api/device-stats/${id}`),
};

// ---- Chats ----
export const chatsAPI = {
    getAll: (deviceId) => api.get('/api/chats', { params: { deviceId } }),
    getMessages: (deviceId, chatJid, page = 1, limit = 50) =>
        api.get(`/api/messages/${deviceId}/${encodeURIComponent(chatJid)}`, { params: { page, limit } }),
    sendMessage: (deviceId, formData) =>
        api.post(`/api/messages/${deviceId}/send`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
    markRead: (deviceId, chatJid) =>
        api.post(`/api/chats/${deviceId}/${encodeURIComponent(chatJid)}/read`),
    // Message actions
    react: (deviceId, data) => api.post(`/api/messages/${deviceId}/react`, data),
    star: (deviceId, data) => api.post(`/api/messages/${deviceId}/star`, data),
    deleteMessage: (deviceId, data) => api.post(`/api/messages/${deviceId}/delete`, data),
    forward: (deviceId, data) => api.post(`/api/messages/${deviceId}/forward`, data),
    edit: (deviceId, data) => api.post(`/api/messages/${deviceId}/edit`, data),
    getReactions: (deviceId, messageId) => api.get(`/api/reactions/${deviceId}/${messageId}`),
    search: (deviceId, query) => api.get(`/api/search/${deviceId}`, { params: { query } }),
};

// ---- Presence ----
export const presenceAPI = {
    sendTyping: (deviceId, data) => api.post(`/api/presence/${deviceId}/typing`, data),
    subscribe: (deviceId, data) => api.post(`/api/presence/${deviceId}/subscribe`, data),
};

// ---- Groups ----
export const groupsAPI = {
    getAll: (deviceId) => api.get(`/api/groups/${deviceId}`),
    getMetadata: (deviceId, groupJid) => api.get(`/api/groups/${deviceId}/${encodeURIComponent(groupJid)}`),
    create: (deviceId, data) => api.post(`/api/groups/${deviceId}/create`, data),
    updateParticipants: (deviceId, groupJid, data) =>
        api.post(`/api/groups/${deviceId}/${encodeURIComponent(groupJid)}/participants`, data),
    update: (deviceId, groupJid, data) =>
        api.put(`/api/groups/${deviceId}/${encodeURIComponent(groupJid)}`, data),
};

// ---- Labels ----
export const labelsAPI = {
    getAll: (deviceId) => api.get(`/api/labels/${deviceId}`),
    updateChat: (deviceId, data) => api.post(`/api/labels/${deviceId}/chat`, data),
};

// ---- Blocklist ----
export const blocklistAPI = {
    getAll: (deviceId) => api.get(`/api/blocklist/${deviceId}`),
    update: (deviceId, data) => api.post(`/api/blocklist/${deviceId}`, data),
};

// ---- Profile ----
export const profileAPI = {
    get: (deviceId, jid) => api.get(`/api/profile/${deviceId}/${encodeURIComponent(jid)}`),
};

// ---- Contacts ----
export const contactsAPI = {
    getAll: (deviceId) => api.get(`/get-contacts/${deviceId}`),
};

// ---- Status ----
export const statusAPI = {
    sendText: (deviceId, data) => api.post(`/send-text-status/${deviceId}`, data),
    sendMedia: (deviceId, formData) =>
        api.post(`/send-media-status/${deviceId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
    getMy: (deviceId) => api.get(`/get-my-status/${deviceId}`),
    getViewers: (statusId) => api.get(`/get-status-viewers/${statusId}`),
};

// ---- Campaigns ----
export const campaignsAPI = {
    getAll: () => api.get('/api/campaigns'),
    getById: (id) => api.get(`/api/campaigns/${id}`),
    create: (formData) =>
        api.post('/api/campaigns', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
    update: (id, formData) =>
        api.put(`/api/campaigns/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
    delete: (id) => api.delete(`/api/campaigns/${id}`),
    start: (id) => api.post(`/api/campaigns/${id}/start`),
    pause: (id) => api.post(`/api/campaigns/${id}/pause`),
    resume: (id) => api.post(`/api/campaigns/${id}/resume`),
    getMessages: (id) => api.get(`/api/campaigns/${id}/messages`),
};

// ---- Contact Lists ----
export const contactListsAPI = {
    getAll: () => api.get('/api/contact-lists'),
    getById: (id) => api.get(`/api/contact-lists/${id}`),
    create: (data) => api.post('/api/contact-lists', data),
    update: (id, data) => api.put(`/api/contact-lists/${id}`, data),
    delete: (id) => api.delete(`/api/contact-lists/${id}`),
    addItems: (id, items) => api.post(`/api/contact-lists/${id}/items`, { items }),
    removeItem: (id, jid) => api.delete(`/api/contact-lists/${id}/items/${encodeURIComponent(jid)}`),
    importCSV: (id, formData) =>
        api.post(`/api/contact-lists/${id}/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }),
};

// ---- Webhooks ----
export const webhooksAPI = {
    getAll: (sessionId) => api.get(`/api/webhooks/${sessionId}`),
    create: (data) => api.post('/api/webhooks', data),
    update: (id, data) => api.put(`/api/webhooks/${id}`, data),
    delete: (id) => api.delete(`/api/webhooks/${id}`),
    test: (id) => api.post(`/api/webhooks/${id}/test`),
    generateApiKey: (sessionId) => api.post(`/api/webhooks/api-key/${sessionId}`),
};

// ---- Calls (VoIP) ----
export const callsAPI = {
    getHistory: (deviceId, params = {}) => api.get(`/api/calls/${deviceId}`, { params }),
    getStats: (deviceId) => api.get(`/api/calls/${deviceId}/stats`),
    getCall: (deviceId, callId) => api.get(`/api/calls/${deviceId}/${callId}`),
    getActive: (deviceId) => api.get(`/api/calls/${deviceId}/active`),
    initiate: (deviceId, data) => api.post(`/api/calls/${deviceId}/initiate`, data),
    accept: (deviceId, data) => api.post(`/api/calls/${deviceId}/accept`, data),
    reject: (deviceId, data) => api.post(`/api/calls/${deviceId}/reject`, data),
    end: (deviceId, data) => api.post(`/api/calls/${deviceId}/end`, data),
    mute: (deviceId, data) => api.post(`/api/calls/${deviceId}/mute`, data),
    cleanup: (deviceId, days = 90) => api.delete(`/api/calls/${deviceId}/cleanup`, { params: { days } }),
};

// ---- Charts/Dashboard ----
export const chartsAPI = {
    getOverview: () => api.get('/chart/overview'),
    getPaises: () => api.get('/chart/paises'),
    getInstalacoes: () => api.get('/chart/instalacoes'),
    getInstalacoesMes: () => api.get('/chart/instalacoesmes'),
    getUltimas: () => api.get('/chart/ultimas'),
    getLang: () => api.get('/chart/lang'),
    getPlataform: () => api.get('/chart/plataform'),
};

// ---- Monitoring ----
export const monitoringAPI = {
    getOverview: () => api.get('/api/monitoring/overview'),
    getMessagesPerDay: (days = 30) => api.get('/api/monitoring/messages-per-day', { params: { days } }),
    getMessagesPerHour: () => api.get('/api/monitoring/messages-per-hour'),
    getMessagesByType: () => api.get('/api/monitoring/messages-by-type'),
    getDevices: () => api.get('/api/monitoring/devices'),
    getTopChats: (limit = 10) => api.get('/api/monitoring/top-chats', { params: { limit } }),
    getCampaignStats: () => api.get('/api/monitoring/campaign-stats'),
    getCallStats: () => api.get('/api/monitoring/call-stats'),
    getActivityLog: (limit = 50) => api.get('/api/monitoring/activity-log', { params: { limit } }),
    getChatbotStats: () => api.get('/api/monitoring/chatbot-stats'),
};

// ---- Debug / Logs ----
export const debugAPI = {
    getSessions: () => api.get('/api/debug/sessions'),
    getChats: (deviceId, params = {}) => api.get(`/api/debug/chats/${deviceId}`, { params }),
    getMessages: (deviceId, chatJid, params = {}) =>
        api.get(`/api/debug/messages/${deviceId}/${encodeURIComponent(chatJid)}`, { params }),
    search: (params = {}) => api.get('/api/debug/search', { params }),
    getSyncStatus: () => api.get('/api/debug/sync-status'),
    getStats: () => api.get('/api/debug/stats'),
    getActivity: (params = {}) => api.get('/api/debug/activity', { params }),
    getMessageDetail: (deviceId, messageId) => api.get(`/api/debug/message/${deviceId}/${messageId}`),
};

export default api;
